import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import api from "../api/client";
import type { User } from "../types";

interface AuthContextType {
  user:     User | null;
  loading:  boolean;
  login:    (username: string, password: string) => Promise<void>;
  logout:   () => Promise<void>;
  isRole:   (...roles: User["role"][]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get<{ data: User }>("/auth/me")
      .then((res) => setUser(res.data.data))
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = async (username: string, password: string) => {
    const res = await api.post<{ data: User }>("/auth/login", { username, password });
    setUser(res.data.data);
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    setUser(null);
  };

  const isRole = (...roles: User["role"][]) =>
    user ? roles.includes(user.role) : false;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, isRole }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
